import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M18 L18.07 — Notification delivery failures.
 *
 * Reads `GET /notifications/deliveries?status=FAILED` (the delivery-tracking
 * endpoint filtered to failures) and offers a retry per row via
 * `POST /notifications/:id/retry`.
 */
interface DeliveryRow {
  readonly id: string;
  readonly channel: string;
  readonly recipient: string;
  readonly status: string;
  readonly attempts: number;
  readonly provider: string;
  readonly updatedAt: string;
}

const FAILURE_STATUSES = ['FAILED', 'BLOCKED', 'BOUNCED'] as const;

export function NotificationsFailuresPage(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DeliveryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('FAILED');
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    try {
      const r = await apiRequest<{ items: DeliveryRow[] }>(
        `/notifications/deliveries?status=${encodeURIComponent(filter)}`, { token },
      );
      setRows(r.items ?? []);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, [token, filter]);

  useEffect(() => { void load(); }, [load]);

  const retry = useCallback(async (id: string) => {
    setRetrying(id);
    try {
      await apiRequest(`/notifications/${encodeURIComponent(id)}/retry`, { method: 'POST', token });
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setRetrying(null); }
  }, [token, load]);

  return (
    <main aria-label="notification-failures">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('notif.failures.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('notif.failures.subtitle')}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <label htmlFor="status-filter" style={{ fontSize: 12 }}>{t('notif.common.status')}</label>
        <select id="status-filter" aria-label="status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {FAILURE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('notif.common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="failures-empty" style={{ color: 'var(--color-text-muted)' }}>{t('notif.failures.empty')}</p>
      ) : (
        <DataGrid<any>
          caption="failures-table"
          rows={rows}
          getRowId={(r) => r.id}
          rowTestId={(r) => `failure-row-${r.id}`}
          columns={[
            { key: 'updated', header: t('notif.common.updated'), value: (r) => r.updatedAt, render: (r) => at(r.updatedAt) },
            { key: 'channel', header: t('notif.common.channel'), value: (r) => r.channel },
            { key: 'recipient', header: t('notif.common.recipient'), value: (r) => r.recipient },
            { key: 'provider', header: t('notif.common.provider'), value: (r) => r.provider },
            { key: 'attempts', header: t('notif.common.attempts'), value: (r) => r.attempts, numeric: true },
            { key: 'status', header: t('notif.common.status'), value: (r) => r.status },
          ]}
          actionsHeader=""
          actions={(r) => (
            <Button type="button" data-testid={`retry-${r.id}`} disabled={retrying === r.id}
                    onClick={() => retry(r.id)} variant="primary">
              {retrying === r.id ? t('notif.failures.retrying') : t('notif.failures.retry')}
            </Button>
          )}
        />
      )}
    </main>
  );
}
